/**
 * Cores dos gráficos, em hexadecimal de verdade (e não variáveis CSS), para
 * que o gráfico baixado como imagem saia igual ao da tela.
 *
 * Gráficos de uma série só (barras): uma cor para todas as barras (a da marca).
 * Corrida: ouro, prata e bronze para os 3 primeiros (a metáfora de pódio que a
 * gerente usou). O validador de paleta passa na separação para daltonismo
 * (ΔE 17) e no contraste, mas reprova "faixa de claridade/croma", porque a
 * prata é cinza de propósito. Por isso a cor nunca é a única pista: cada
 * carro tem o número da posição e as faixas estão em ordem.
 *
 * Lê: a preferência de tema do sistema (claro/escuro).
 * Usado por: graficos/*.tsx e telas que mostram gráficos.
 */
import { useEffect, useState } from 'react';

export interface Cores {
  superficie: string;
  tinta: string;
  tinta2: string;
  tinta3: string;
  grade: string;
  eixo: string;
  barra: string;
  barraDestaque: string;
  trilho: string;
  ritmo: string;
  supermeta: string;
  marcaSuave: string;
  marcaForte: string;
  pista: string;
  pistaFaixa: string;
  pistaGrade: string;
  pistaTexto: string;
  pistaTextoApagado: string;
  ritmoPista: string;
  ouro: string;
  prata: string;
  bronze: string;
  carro: string;
  carroParado: string;
  roda: string;
  aro: string;
  foco: string;
}

export const CORES_CLARO: Cores = {
  superficie: '#ffffff',
  tinta: '#1d1630',
  tinta2: '#554e66',
  tinta3: '#857f93',
  grade: '#ebe7f1',
  eixo: '#cfc8db',
  barra: '#8e6cc9',
  barraDestaque: '#5b3a9b',
  trilho: '#ece5f8',
  ritmo: '#b7791f',
  supermeta: '#5b3a9b',
  marcaSuave: '#ece5f8',
  marcaForte: '#5b3a9b',
  pista: '#2e2a37',
  pistaFaixa: 'rgba(255,255,255,0.18)',
  pistaGrade: 'rgba(255,255,255,0.08)',
  pistaTexto: '#ffffff',
  pistaTextoApagado: '#d6d2df',
  ritmoPista: '#f2c14e',
  ouro: '#e0ac2b',
  prata: '#c3c8d2',
  bronze: '#cd8a4f',
  carro: '#8e6cc9',
  carroParado: '#a9a4b5',
  roda: '#17141d',
  aro: '#d9d6df',
  foco: '#7b55c4',
};

export const CORES_ESCURO: Cores = {
  ...CORES_CLARO,
  superficie: '#1b1822',
  tinta: '#f4f1fa',
  tinta2: '#c4bdd3',
  tinta3: '#8f889e',
  grade: '#2c2836',
  eixo: '#3d3849',
  barra: '#a58be6',
  barraDestaque: '#c7b5f2',
  trilho: '#2c2540',
  ritmo: '#f2c14e',
  supermeta: '#c7b5f2',
  marcaSuave: '#2c2540',
  marcaForte: '#c7b5f2',
  pista: '#100e14',
  foco: '#a58be6',
};

const CONSULTA_ESCURO = '(prefers-color-scheme: dark)';

/** Cores do tema atual; muda sozinho se o sistema trocar de claro para escuro. */
export function usarCores(): Cores {
  const [escuro, setEscuro] = useState(() => window.matchMedia(CONSULTA_ESCURO).matches);
  useEffect(() => {
    const consulta = window.matchMedia(CONSULTA_ESCURO);
    const aoMudar = () => setEscuro(consulta.matches);
    consulta.addEventListener('change', aoMudar);
    return () => consulta.removeEventListener('change', aoMudar);
  }, []);
  return escuro ? CORES_ESCURO : CORES_CLARO;
}
